import { useEffect, useState } from "react";
import { properties } from "../utils/Properties_es";

export const useFileUpload = (onFileSelect, options = {}) => {
    const {
        accept = "",
        maxSizeBytes = 10 * 1024 * 1024,
        invalidTypeMessage = properties.upload_error_invalid_format,
        invalidSizeMessage = properties.upload_error_max_size
    } = options;
    const [file, setFile] = useState(null);
    const [previewURL, setPreviewURL] = useState(null);
    const [isDragging, setIsDragging] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const isAcceptedFile = (selectedFile) => {
        if (!selectedFile || !accept) return true;

        const acceptedTypes = accept
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);

        if (acceptedTypes.length === 0) return true;

        return acceptedTypes.some((allowedType) => {
            if (allowedType === '*/*') return true;

            if (allowedType.startsWith('.')) {
                return selectedFile.name?.toLowerCase().endsWith(allowedType.toLowerCase());
            }

            if (allowedType.endsWith('/*')) {
                const typePrefix = allowedType.slice(0, -1);
                return (selectedFile.type || '').startsWith(typePrefix);
            }

            return (selectedFile.type || '') === allowedType;
        });
    };

    const validateFile = (selectedFile) => {
        if (!selectedFile) return null;

        if (!isAcceptedFile(selectedFile)) {
            return invalidTypeMessage;
        }

        if (maxSizeBytes && selectedFile.size > maxSizeBytes) {
            return invalidSizeMessage;
        }

        return null;
    };

    const storeFile = (selectedFile) => {
        setErrorMessage("");
        setFile(selectedFile);
        const objectURL = URL.createObjectURL(selectedFile);
        setPreviewURL(objectURL);

        if (onFileSelect) {
            onFileSelect(selectedFile);
        }
    };

    const handleFile = (selectedFile) => {
        const validationError = validateFile(selectedFile);

        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        if (selectedFile) {
            storeFile(selectedFile);
        }
    };

    const handleChange = (e) => {
        const selectedFile = e.target.files[0];
        const validationError = validateFile(selectedFile);

        if (validationError) {
            setErrorMessage(validationError);
            e.target.value = "";
            return;
        }

        if (selectedFile) {
            storeFile(selectedFile);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    }

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    }

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        handleFile(droppedFile);
    }

    useEffect(() => {
        return () => {
            if (previewURL) URL.revokeObjectURL(previewURL);
        }
    }, [previewURL]);

    return {
        file,
        previewURL,
        isDragging,
        errorMessage,
        handleChange,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        setFile,
        setPreviewURL
    }
}
